import {memo, useEffect, useRef, useState} from 'react'
import type {DiffEntry, DiffUser} from '@github-ui/diff-lines/types'
import {DiffPlaceholder} from '@github-ui/diffs/DiffParts'
import styles from './PullRequestDiffsList.module.css'
import type {CommentingImplementation, MarkerNavigationImplementation} from '@github-ui/conversations'
import type {PullRequest} from '../types'
import {FilesChangedFilterBlankSlate} from './FilesChangedFilterBlankSlate'
import {useSelectedFullDiffHash} from '@github-ui/diff-lines/use-selected-diff-hash'
import {ssrSafeWindow} from '@github-ui/ssr-utils'
import type {Repository} from '@github-ui/current-repository'
import {PullRequestDiff} from './PullRequestDiff'

export interface PullRequestDiffsListProps {
  commentBoxConfig: CommentingImplementation['commentBoxConfig']
  contextLinesURL: string
  currentUser: DiffUser
  diffs: DiffEntry[]
  headBranchName: string
  pullRequest: PullRequest
  repository: Pick<Repository, 'id' | 'name' | 'ownerLogin'>
  commentBatchPending: boolean
}

const headerHeight = 41
const headerWrapperPadding = 16
const baseDiffHeight = headerHeight + headerWrapperPadding
const stickyHeaderHeight = 60 // height to be used when scrolling, the 4px difference looks nicer
export const PullRequestDiffsList = memo(PullRequestDiffsListUnmemoized)

function PullRequestDiffsListUnmemoized({
  commentBoxConfig,
  contextLinesURL,
  currentUser,
  diffs,
  headBranchName,
  pullRequest,
  repository,
  commentBatchPending,
}: PullRequestDiffsListProps) {
  const [diffManuallyExpanded, __] = useState(false)
  const selectedPathDigest = useSelectedFullDiffHash()
  const previouslyFocusedDiff = useRef('')
  const diffsParentRef = useRef<HTMLDivElement>(null)
  const markerNavigationImplementation: MarkerNavigationImplementation = {
    incrementActiveMarker: () => {},
    decrementActiveMarker: () => {},
    filteredMarkers: [],
    onActivateGlobalMarkerNavigation: () => {},
    activeGlobalMarkerID: undefined,
  }

  useEffect(() => {
    const isHighlightingRightRow = selectedPathDigest.includes('R')
    const isHighlightingLeftRow = selectedPathDigest.includes('L')
    const isHighlightingRow = isHighlightingLeftRow || isHighlightingRightRow
    let pathDigestWithNoLineNumberOrHash = isHighlightingRow
      ? isHighlightingLeftRow
        ? selectedPathDigest.split('L')[0]
        : selectedPathDigest.split('R')[0]
      : selectedPathDigest
    pathDigestWithNoLineNumberOrHash = pathDigestWithNoLineNumberOrHash?.replace('#', '')
    if (pathDigestWithNoLineNumberOrHash === previouslyFocusedDiff.current) {
      //we are focused on the same file, no need to do the work again
      return
    }
    previouslyFocusedDiff.current = pathDigestWithNoLineNumberOrHash ?? ''
    //remove the leading hash if it exists, also trim the secondary line numbers off so that the first line number can
    // be focused and scrolled to
    const pathDigestWithoutHashOrDash = selectedPathDigest.replace('#', '').split('-')[0]
    const indexOfDiff = getDiffWithPathDigest(pathDigestWithNoLineNumberOrHash ?? selectedPathDigest, diffs)
    if (indexOfDiff === -1) {
      return
    }
    if (isHighlightingRow && focusRowElement(diffsParentRef, pathDigestWithoutHashOrDash ?? '')) {
      //if we are in here, then nothing else to do, as the user was selecting a row already
    } else {
      // otherwise we couldn't find the row to highlight (was a context row)  so default to just highlighting the file itself
      const allDiffLinesElements = diffsParentRef.current?.querySelectorAll(
        `[data-diff-anchor=diff-${pathDigestWithNoLineNumberOrHash}]`,
      )
      if (allDiffLinesElements && allDiffLinesElements.length === 1) {
        const elementToScrollTo = allDiffLinesElements[0] as HTMLElement
        const yOffset =
          elementToScrollTo.getBoundingClientRect().top +
          (ssrSafeWindow?.scrollY ?? 0) -
          baseDiffHeight -
          stickyHeaderHeight
        ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
        const collapseButton = diffsParentRef.current?.querySelectorAll(
          `[aria-label="collapse file: ${diffs[indexOfDiff]?.path}"]`,
        )
        if (collapseButton && collapseButton.length === 1) {
          const collapseButtonToFocus = collapseButton[0] as HTMLElement
          collapseButtonToFocus.focus()
        }
      } else {
        //in case the diff was collapsed, there wouldn't be any diff lines to find, so we
        //instead scroll to the expand button
        const expandButton = diffsParentRef.current?.querySelectorAll(
          `[aria-label="expand file: ${diffs[indexOfDiff]?.path}"]`,
        )
        if (expandButton && expandButton.length === 1) {
          const expandButtonToFocus = expandButton[0] as HTMLElement
          const yOffset =
            expandButtonToFocus.getBoundingClientRect().top +
            (ssrSafeWindow?.scrollY ?? 0) -
            baseDiffHeight -
            stickyHeaderHeight
          ssrSafeWindow?.scrollTo({top: yOffset, left: 0})
          expandButtonToFocus.focus()
        }
      }
    }
  }, [selectedPathDigest, diffs])

  // TODO: implement custom find if necessary, and if not, this can stay undefined
  // but we probably will want custom find for the scenario where a user is viewing
  // a PR that is too big and we need to populate a list of search results from within
  // the PR
  const focusedSearchResult = undefined

  return (
    <div ref={diffsParentRef} data-hpc>
      {diffs.map(diff => {
        return (
          <div key={diff.pathDigest} className={styles.entry}>
            <PullRequestDiff
              commentBoxConfig={commentBoxConfig}
              commentBatchPending={commentBatchPending}
              headBranchName={headBranchName}
              basePath={pullRequest.pathName}
              changeType={diff.status}
              collapsed={diff.collapsed ?? false}
              commentingEnabled
              contextLinesURL={contextLinesURL}
              currentUser={currentUser}
              diffContext="pr"
              diffLines={diff.diffLines}
              diffManuallyExpanded={diffManuallyExpanded}
              diffMatches={diff.diffMatches}
              diffSize={diff.diffSize}
              focusedSearchResult={focusedSearchResult}
              helpUrl={diff.helpUrl}
              isBinary={diff.isBinary}
              isSubmodule={diff.isSubmodule}
              isTooBig={diff.isTooBig}
              linesAdded={diff.linesAdded}
              linesChanged={diff.linesChanged}
              linesDeleted={diff.linesDeleted}
              markerNavigationImplementation={markerNavigationImplementation}
              newCommitOid={diff.newCommitOid}
              newTreeEntry={diff.newTreeEntry}
              objectId={diff.objectId}
              oldCommitOid={diff.oldCommitOid}
              oldTreeEntry={diff.oldTreeEntry}
              path={diff.path}
              pathDigest={diff.pathDigest}
              repository={repository}
              richDiff={diff.richDiff}
              status={diff.status}
              submodule={diff.submodule}
              truncatedReason={diff.truncatedReason}
            />
          </div>
        )
      })}
      {diffs.length === 0 && <FilesChangedFilterBlankSlate />}
      {/* DiffPlaceholder is used for the skeleton placeholder when a diff isn't loaded, it needs to be
  somewhere on the page so that it can be drawn from within the diff lines component.  */}
      <DiffPlaceholder />
    </div>
  )
}

function getDiffWithPathDigest(pathDigest: string, diffEntries: DiffEntry[]) {
  for (let i = 0; i < diffEntries.length; i++) {
    if (pathDigest === diffEntries[i]?.pathDigest) return i
  }
  return -1
}

function focusRowElement(diffsParentRef: React.RefObject<HTMLDivElement>, selectedPathDigest: string) {
  const lineWithinDiffToHighlight = diffsParentRef.current?.querySelectorAll(
    `[data-line-anchor=diff-${selectedPathDigest}]`,
  )
  if (lineWithinDiffToHighlight && lineWithinDiffToHighlight.length === 1) {
    const rowToFocus = lineWithinDiffToHighlight[0] as HTMLElement
    rowToFocus.focus()
    return true
  }
  return false
}

try{ PullRequestDiffsList.displayName ||= 'PullRequestDiffsList' } catch {}
try{ PullRequestDiffsListUnmemoized.displayName ||= 'PullRequestDiffsListUnmemoized' } catch {}